import axiosInstance from '../../../config/axiosInstance';
import type {
  StacBootstrapRequest,
  StacContext,
  StacDay,
  StacDedication,
  StacManagedTask,
  StacManagedTaskCreateRequest,
  StacManagedTaskMutationResponse,
  StacOrgEditorManagerUpdateRequest,
  StacOrgEditorMutationResponse,
  StacOrgEditorState,
  StacOrgEditorUnitHistory,
  StacOrgEditorWorkerHistory,
  StacOrgEditorUnitCreateRequest,
  StacOrgEditorUnitMoveRequest,
  StacOrgEditorUnitRenameRequest,
  StacOrgEditorWorkerPlacementRequest,
  StacOrgHistoryEntry,
  StacOrgManager,
  StacOrgMoveRequest,
  StacOrgMutationResponse,
  StacOrgTree,
  StacOrgUnitCreateRequest,
  StacProject,
  StacProjectCreateRequest,
  StacQuickTaskRequest,
  StacQuickTaskResponse,
  StacTask,
  StacTaskAssignmentsUpdateRequest,
  StacTaskProjectMutationResponse,
  StacTaskProjectUpdateRequest,
  StacTeamReportingDay,
  StacTeamReportingScope,
  StacWorker,
  StacWorkerCreateRequest,
  StacTimeEntryUpdateRequest,
  StacTimeOperationResponse,
} from '../types/stac';


export async function getStacContext(): Promise<StacContext> {
  const { data } = await axiosInstance.get<StacContext>(
    '/stac/context'
  );

  return data;
}


export async function bootstrapStacContext(
  payload: StacBootstrapRequest,
): Promise<StacContext> {
  const { data } = await axiosInstance.post<StacContext>(
    '/stac/bootstrap',
    payload,
  );

  return data;
}


export async function listStacMyTasks(): Promise<StacTask[]> {
  const { data } = await axiosInstance.get<StacTask[]>(
    '/stac/me/tasks'
  );

  return data;
}


export async function getStacMyDay(params: {
  day: string;
  timezone: string;
}): Promise<StacDay> {
  const { data } = await axiosInstance.get<StacDay>(
    '/stac/me/day',
    {
      params: {
        day: params.day,
        timezone: params.timezone,
      },
    },
  );

  return data;
}


export async function getStacMyDedication(params: {
  from: string;
  to: string;
  timezone: string;
}): Promise<StacDedication> {
  const { data } = await axiosInstance.get<StacDedication>(
    '/stac/me/dedication',
    {
      params: {
        from: params.from,
        to: params.to,
        timezone: params.timezone,
      },
    },
  );

  return data;
}


export async function listStacManagedWorkers(): Promise<StacWorker[]> {
  const { data } = await axiosInstance.get<StacWorker[]>(
    '/stac/manage/workers'
  );

  return data;
}


export async function createStacManagedWorker(
  payload: StacWorkerCreateRequest,
): Promise<StacWorker> {
  const { data } = await axiosInstance.post<StacWorker>(
    '/stac/manage/workers',
    payload,
  );

  return data;
}


export async function getStacManagedOrganization(): Promise<StacOrgTree> {
  const { data } = await axiosInstance.get<StacOrgTree>(
    '/stac/manage/organization'
  );

  return data;
}


export async function createStacOrganizationUnit(
  payload: StacOrgUnitCreateRequest,
): Promise<StacOrgMutationResponse> {
  const { data } =
    await axiosInstance.post<StacOrgMutationResponse>(
      '/stac/manage/organization/units',
      payload,
    );

  return data;
}


export async function moveStacOrganizationNode(
  nodeId: string,
  payload: StacOrgMoveRequest,
): Promise<StacOrgMutationResponse> {
  const { data } =
    await axiosInstance.put<StacOrgMutationResponse>(
      `/stac/manage/organization/nodes/${encodeURIComponent(nodeId)}/parent`,
      payload,
    );

  return data;
}


export async function getStacOrganizationNodeHistory(
  nodeId: string,
): Promise<StacOrgHistoryEntry[]> {
  const { data } =
    await axiosInstance.get<StacOrgHistoryEntry[]>(
      `/stac/manage/organization/nodes/${encodeURIComponent(nodeId)}/history`,
    );

  return data;
}


export async function getStacOrganizationManager(
  workerId: string,
  at?: string | null,
): Promise<StacOrgManager | null> {
  const { data } =
    await axiosInstance.get<StacOrgManager | null>(
      `/stac/manage/organization/workers/${encodeURIComponent(workerId)}/manager`,
      {
        params: at
          ? {
            at,
          }
          : undefined,
      },
    );

  return data;
}


export async function getStacOrganizationEditor(): Promise<StacOrgEditorState> {
  const { data } = await axiosInstance.get<StacOrgEditorState>(
    '/stac/manage/organization/editor'
  );

  return data;
}


export async function createStacOrganizationEditorUnit(
  payload: StacOrgEditorUnitCreateRequest,
): Promise<StacOrgEditorMutationResponse> {
  const { data } =
    await axiosInstance.post<StacOrgEditorMutationResponse>(
      '/stac/manage/organization/editor/units',
      payload,
    );

  return data;
}


export async function renameStacOrganizationEditorUnit(
  unitId: string,
  payload: StacOrgEditorUnitRenameRequest,
): Promise<StacOrgEditorMutationResponse> {
  const { data } =
    await axiosInstance.patch<StacOrgEditorMutationResponse>(
      `/stac/manage/organization/editor/units/${encodeURIComponent(unitId)}`,
      payload,
    );

  return data;
}


export async function moveStacOrganizationEditorUnit(
  unitId: string,
  payload: StacOrgEditorUnitMoveRequest,
): Promise<StacOrgEditorMutationResponse> {
  const { data } =
    await axiosInstance.put<StacOrgEditorMutationResponse>(
      `/stac/manage/organization/editor/units/${encodeURIComponent(unitId)}/parent`,
      payload,
    );

  return data;
}


export async function placeStacOrganizationEditorWorker(
  workerId: string,
  payload: StacOrgEditorWorkerPlacementRequest,
): Promise<StacOrgEditorMutationResponse> {
  const { data } =
    await axiosInstance.put<StacOrgEditorMutationResponse>(
      `/stac/manage/organization/editor/workers/${encodeURIComponent(workerId)}/placement`,
      payload,
    );

  return data;
}


export async function updateStacOrganizationEditorManager(
  unitId: string,
  payload: StacOrgEditorManagerUpdateRequest,
): Promise<StacOrgEditorMutationResponse> {
  const { data } =
    await axiosInstance.put<StacOrgEditorMutationResponse>(
      `/stac/manage/organization/editor/units/${encodeURIComponent(unitId)}/manager`,
      payload,
    );

  return data;
}


export async function archiveStacOrganizationEditorUnit(
  unitId: string,
): Promise<StacOrgEditorMutationResponse> {
  const { data } =
    await axiosInstance.post<StacOrgEditorMutationResponse>(
      `/stac/manage/organization/editor/units/${encodeURIComponent(unitId)}/archive`,
    );

  return data;
}


export async function getStacOrganizationEditorUnitHistory(
  unitId: string,
): Promise<StacOrgEditorUnitHistory> {
  const { data } =
    await axiosInstance.get<StacOrgEditorUnitHistory>(
      `/stac/manage/organization/editor/units/${encodeURIComponent(unitId)}/history`,
    );

  return data;
}


export async function getStacOrganizationEditorWorkerHistory(
  workerId: string,
): Promise<StacOrgEditorWorkerHistory> {
  const { data } =
    await axiosInstance.get<StacOrgEditorWorkerHistory>(
      `/stac/manage/organization/editor/workers/${encodeURIComponent(workerId)}/history`,
    );

  return data;
}


export async function getStacTeamReportingScope(): Promise<StacTeamReportingScope> {
  const { data } =
    await axiosInstance.get<StacTeamReportingScope>(
      '/stac/reporting/team/scope'
    );

  return data;
}


export async function getStacTeamReportingDay(
  params: {
    day: string;
    timezone: string;
  },
): Promise<StacTeamReportingDay> {
  const { data } =
    await axiosInstance.get<StacTeamReportingDay>(
      '/stac/reporting/team/day',
      {
        params: {
          day: params.day,
          timezone:
            params.timezone,
        },
      },
    );

  return data;
}


export async function listStacManagedProjects(): Promise<StacProject[]> {
  const { data } = await axiosInstance.get<StacProject[]>(
    '/stac/manage/projects'
  );

  return data;
}


export async function createStacManagedProject(
  payload: StacProjectCreateRequest,
): Promise<StacProject> {
  const { data } = await axiosInstance.post<StacProject>(
    '/stac/manage/projects',
    payload,
  );

  return data;
}


export async function listStacManagedTasks(): Promise<StacManagedTask[]> {
  const { data } = await axiosInstance.get<StacManagedTask[]>(
    '/stac/manage/tasks'
  );

  return data;
}


export async function createStacManagedTask(
  payload: StacManagedTaskCreateRequest,
): Promise<StacManagedTaskMutationResponse> {
  const { data } = await axiosInstance.post<StacManagedTaskMutationResponse>(
    '/stac/manage/tasks',
    payload,
  );

  return data;
}


export async function updateStacManagedTaskAssignments(
  taskId: string,
  payload: StacTaskAssignmentsUpdateRequest,
): Promise<StacManagedTaskMutationResponse> {
  const { data } = await axiosInstance.put<StacManagedTaskMutationResponse>(
    `/stac/manage/tasks/${encodeURIComponent(taskId)}/assignments`,
    payload,
  );

  return data;
}


export async function updateStacManagedTaskProject(
  taskId: string,
  payload: StacTaskProjectUpdateRequest,
): Promise<StacTaskProjectMutationResponse> {
  const { data } =
    await axiosInstance.put<StacTaskProjectMutationResponse>(
      `/stac/manage/tasks/${encodeURIComponent(taskId)}/project`,
      payload,
    );

  return data;
}


export async function createStacQuickTask(
  payload: StacQuickTaskRequest,
): Promise<StacQuickTaskResponse> {
  const { data } = await axiosInstance.post<StacQuickTaskResponse>(
    '/stac/tasks/quick',
    payload,
  );

  return data;
}


export async function startStacTask(
  taskId: string,
): Promise<StacTimeOperationResponse> {
  const { data } = await axiosInstance.post<StacTimeOperationResponse>(
    `/stac/tasks/${encodeURIComponent(taskId)}/start`,
  );

  return data;
}


export async function finishStacCurrentActivity(): Promise<StacTimeOperationResponse> {
  const { data } = await axiosInstance.post<StacTimeOperationResponse>(
    '/stac/time/current/finish',
  );

  return data;
}


export async function updateStacTimeEntry(
  entryId: string,
  payload: StacTimeEntryUpdateRequest,
): Promise<StacTimeOperationResponse> {
  const { data } = await axiosInstance.put<StacTimeOperationResponse>(
    `/stac/time/${encodeURIComponent(entryId)}`,
    payload,
  );

  return data;
}
